import React, { useState } from 'react';
import { Check, ChevronDown, ExclamationCircleFill } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';
import {
  DASHBOARD_CARD_RADIUS,
  DASHBOARD_SECTION_TITLE_STYLE,
  getDashboardCardStyle,
} from '../../constants/dashboardLayout';
import { formatSignedCurrency, formatSignedPercent } from '../../utils/plFormat';

function toneColor(tone, colors) {
  if (tone === 'ok') return colors.success || colors.chartUp;
  if (tone === 'warning') return colors.warning || '#F59E0B';
  return colors.danger || colors.chartDown;
}

function plTone(value, colors, up, down) {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return colors.text;
  return num > 0 ? up : down;
}

function PnlBlock({ label, amount, percent, basis, testId, percentTestId, align }) {
  const { colors } = useTheme();
  const up = colors.chartUp || colors.success;
  const down = colors.chartDown || colors.danger;
  const tone = plTone(amount, colors, up, down);
  return (
    <div data-testid={testId} style={{ minWidth: 0, textAlign: align }}>
      <div style={{ ...DASHBOARD_SECTION_TITLE_STYLE, color: colors.textMuted, marginBottom: 6 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: '1.35rem',
          fontWeight: 700,
          letterSpacing: '-0.03em',
          fontVariantNumeric: 'tabular-nums',
          color: tone,
          lineHeight: 1.1,
        }}
      >
        {formatSignedCurrency(amount)}
      </div>
      <div
        data-testid={percentTestId}
        style={{
          marginTop: 4,
          fontSize: '0.84rem',
          fontWeight: 650,
          fontVariantNumeric: 'tabular-nums',
          color: plTone(percent ?? amount, colors, up, down),
        }}
      >
        {percent == null ? '—' : formatSignedPercent(percent)}
        {basis ? (
          <span style={{ marginLeft: 6, fontWeight: 500, color: colors.textMuted }}>{basis}</span>
        ) : null}
      </div>
    </div>
  );
}

function Metric({ label, value, sub, color, testId }) {
  const { colors } = useTheme();
  return (
    <div data-testid={testId} style={{ minWidth: 0 }}>
      <div
        style={{
          ...DASHBOARD_SECTION_TITLE_STYLE,
          color: colors.textMuted,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '1.05rem',
          fontWeight: 700,
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
          color: color || colors.text,
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
      {sub ? (
        <div style={{ marginTop: 4, fontSize: '0.78rem', color: colors.textLight || colors.textMuted }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

/** Health-style snapshot of the open book: P&L, winners/losers, and missing stops. */
export default function PositionsOverviewSummaryCard({ stats }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const up = colors.chartUp || colors.success;
  const down = colors.chartDown || colors.danger;
  const missing = stats.missingStopCount;
  const tone = missing > 0 ? 'warning' : 'ok';
  const accent = missing > 0 ? '#E39A45' : toneColor(tone, colors);
  const headline = missing > 0
    ? (missing === 1 ? '1 position missing a stop' : `${missing} positions missing a stop`)
    : 'All positions protected';
  // Total P&L has its own readout to the right, so the subline carries stop coverage instead.
  const subline = missing > 0
    ? `${stats.count} open · ${stats.count - missing} protected`
    : `${stats.count} open`;

  return (
    <section
      data-testid="positions-overview-summary"
      style={{
        ...getDashboardCardStyle(colors, { radius: DASHBOARD_CARD_RADIUS }),
        marginBottom: 24,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: isMobile ? '16px' : '18px 22px',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: isMobile ? 14 : 24,
          background: missing > 0
            ? 'linear-gradient(90deg, rgba(92, 48, 12, 0.72), rgba(42, 24, 10, 0.28) 58%, transparent)'
            : `linear-gradient(90deg, ${accent}14, transparent 55%)`,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              ...DASHBOARD_SECTION_TITLE_STYLE,
              color: missing > 0 ? accent : colors.textMuted,
              marginBottom: 6,
            }}
          >
            {missing > 0 ? 'Positions missing a stop' : 'Open positions'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {tone === 'ok'
              ? <Check size={22} style={{ color: accent, flexShrink: 0 }} />
              : <ExclamationCircleFill size={20} color={accent} style={{ flexShrink: 0 }} />}
            <div style={{ minWidth: 0 }}>
              <div
                data-testid="positions-overview-headline"
                style={{
                  fontSize: isMobile ? '1.15rem' : '1.35rem',
                  fontWeight: 700,
                  color: colors.text,
                  lineHeight: 1.2,
                }}
              >
                {headline}
              </div>
              <div
                data-testid="positions-overview-subline"
                style={{
                  fontSize: '0.84rem',
                  color: colors.textLight || colors.textMuted,
                  marginTop: 4,
                  lineHeight: 1.35,
                }}
              >
                {subline}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            gap: isMobile ? 20 : 28,
            justifyContent: isMobile ? 'flex-start' : 'flex-end',
          }}
        >
          <PnlBlock
            label="Today"
            amount={stats.todayPnl}
            percent={stats.todayPnlPercent}
            basis="of value"
            testId="positions-overview-today"
            align={isMobile ? 'left' : 'right'}
          />
          <PnlBlock
            label="Overall P&L"
            amount={stats.totalPnl}
            percent={stats.totalPnlPercent}
            basis="of cost"
            testId="positions-overview-total-pnl"
            percentTestId="positions-overview-total-pnl-percent"
            align={isMobile ? 'left' : 'right'}
          />
        </div>
      </div>

      <button
        type="button"
        data-testid="positions-overview-breakdown-toggle"
        aria-expanded={breakdownOpen}
        onClick={() => setBreakdownOpen((open) => !open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: isMobile ? '12px 16px' : '12px 22px',
          border: 'none',
          borderTop: breakdownOpen ? 'none' : undefined,
          background: 'transparent',
          color: colors.textMuted,
          cursor: 'pointer',
          fontSize: '0.78rem',
          fontWeight: 650,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        Position Statistics
        <ChevronDown
          size={14}
          style={{
            transform: breakdownOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 160ms ease',
          }}
        />
      </button>

      {breakdownOpen ? (
      <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
          gap: isMobile ? 16 : 20,
          padding: isMobile ? '0 16px 16px' : '0 22px 18px',
        }}
      >
        <Metric
          testId="positions-overview-avg-winner"
          label="Average winner"
          value={stats.avgWinner == null ? '—' : formatSignedCurrency(stats.avgWinner)}
          sub={stats.winnerCount ? `${stats.winnerCount} winning` : 'No winners'}
          color={stats.avgWinner == null ? colors.textMuted : up}
        />
        <Metric
          testId="positions-overview-avg-loser"
          label="Average loser"
          value={stats.avgLoser == null ? '—' : formatSignedCurrency(stats.avgLoser)}
          sub={stats.loserCount ? `${stats.loserCount} losing` : 'No losers'}
          color={stats.avgLoser == null ? colors.textMuted : down}
        />
        <Metric
          testId="positions-overview-best"
          label="Biggest winner"
          value={stats.biggestWinner ? formatSignedCurrency(stats.biggestWinner.pnl) : '—'}
          sub={stats.biggestWinner?.symbol || '—'}
          color={stats.biggestWinner ? up : colors.textMuted}
        />
        <Metric
          testId="positions-overview-worst"
          label="Biggest loser"
          value={stats.biggestLoser ? formatSignedCurrency(stats.biggestLoser.pnl) : '—'}
          sub={stats.biggestLoser?.symbol || '—'}
          color={stats.biggestLoser ? down : colors.textMuted}
        />
      </div>
      <div
        data-testid="positions-overview-secondary"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px 0',
          margin: isMobile ? '8px 16px 0' : '10px 22px 0',
          padding: isMobile ? '12px 0 16px' : '14px 0 18px',
          borderTop: `1px solid ${colors.border}`,
          fontSize: '0.78rem',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {[
          ['Missing stops', String(stats.missingStopCount ?? 0), missing > 0 ? (colors.warning || '#F59E0B') : colors.text],
          ['Avg P&L', stats.avgPnl == null ? '—' : formatSignedCurrency(stats.avgPnl), plTone(stats.avgPnl, colors, up, down)],
          ['Exposure', formatSignedCurrency(stats.totalValue || 0).replace(/^\+/, ''), colors.text],
          ['Of account', stats.exposurePercent == null ? '—' : `${stats.exposurePercent.toFixed(1)}%`, colors.text],
          ['Protected', `${stats.protectedCount ?? 0}/${stats.count}`, colors.text],
          ['Long', String(stats.longCount ?? 0), colors.text],
          ['Short', String(stats.shortCount ?? 0), colors.text],
        ].map(([label, value, color], index) => (
          <span key={label} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {index > 0 ? (
              <span
                aria-hidden
                style={{
                  width: 1,
                  height: 14,
                  margin: '0 14px',
                  backgroundColor: colors.border,
                  flexShrink: 0,
                }}
              />
            ) : null}
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'baseline' }}>
              <span style={{ color: colors.textMuted }}>{label}</span>
              <strong style={{ color, fontWeight: 650 }}>{value}</strong>
            </span>
          </span>
        ))}
      </div>
      </>
      ) : null}
    </section>
  );
}
